import LegalLayout from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="Legal · Terms"
      lastUpdated="May 2026"
    >
      <p>
        Welcome to TeamNest. These Terms of Service (<strong>"Terms"</strong>)
        govern your access to and use of the TeamNest web app, iOS app,
        Android app and related services (collectively, the
        <strong> "Service"</strong>) operated by TeamNest
        (<strong>"we"</strong>, <strong>"us"</strong>). By creating an
        account, you agree to these Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 13 years old (16 in the EU) to use TeamNest. By
        using the Service you represent that you have the legal authority to
        accept these Terms on behalf of yourself or the organization you
        represent.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You are responsible for safeguarding your password and for all activity that occurs under your account.</li>
        <li>You must provide accurate and current contact information.</li>
        <li>You may not share your account with another person. Each user must have their own account.</li>
        <li>Notify us at <a href="mailto:support@teamnest.ai">support@teamnest.ai</a> if you suspect unauthorized access.</li>
      </ul>

      <h2>3. Subscription, billing, and cancellation</h2>
      <ul>
        <li>TeamNest offers a free tier plus paid Pro and Team plans. Pricing is shown in-app at the moment of purchase.</li>
        <li>Paid plans renew automatically each month or year (per your selection) until cancelled.</li>
        <li>You can cancel at any time from <strong>Settings → Billing → Customer Portal</strong>. Cancellation takes effect at the end of the current billing period.</li>
        <li>We offer a <strong>14-day refund</strong> on first-time subscription purchases if you have not used more than 50 AI credits.</li>
        <li>AI credit packs are non-refundable once consumed.</li>
        <li>Taxes (GST, VAT, sales tax) are added where required by law.</li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree <strong>not</strong> to:</p>
      <ul>
        <li>Use TeamNest to send spam, harass, defame, threaten, or impersonate anyone.</li>
        <li>Upload malware, viruses or any content that violates the law or third-party rights.</li>
        <li>Reverse-engineer, decompile, or attempt to extract source code, except where allowed by law.</li>
        <li>Scrape or programmatically extract data from the Service except via official APIs.</li>
        <li>Use the AI features to generate content that is illegal, sexually explicit involving minors, instructions for weapons, or hate speech.</li>
        <li>Probe, scan or test the vulnerability of the Service without our written permission (responsible disclosure to <a href="mailto:security@teamnest.ai">security@teamnest.ai</a> is welcome).</li>
        <li>Use TeamNest to train competing AI models without our written consent.</li>
      </ul>
      <p>
        We reserve the right to suspend or terminate any account that violates
        these rules, with or without notice.
      </p>

      <h2>5. Your content</h2>
      <ul>
        <li>You retain all ownership of the content you upload, send or generate inside TeamNest.</li>
        <li>You grant us a limited, worldwide, royalty-free license to host, store, transmit, display, reproduce and modify your content solely to operate and improve the Service.</li>
        <li>You represent that you have the rights to upload any content you submit.</li>
        <li>You can export or delete your content at any time from your account settings.</li>
      </ul>

      <h2>6. AI-generated content</h2>
      <ul>
        <li>The Service uses third-party large language models (OpenAI, Anthropic, Google, Perplexity, DeepSeek, xAI). Outputs may be inaccurate, biased, or out of date.</li>
        <li>You are responsible for reviewing AI outputs before relying on them for any decision, especially in legal, medical, financial or safety-critical contexts.</li>
        <li>You agree not to present AI outputs as the work of a human in a deceptive manner where disclosure is legally required.</li>
        <li>Ownership of AI-generated content is governed by each provider's terms. Generally, outputs you generate are owned by you to the extent permitted by law.</li>
      </ul>

      <h2>7. Intellectual property</h2>
      <p>
        TeamNest, the "TN" logo, the teamnest.ai name, and all software,
        designs and content we create remain the exclusive property of
        TeamNest. Nothing in these Terms grants you a license to use our trademarks
        without our prior written permission.
      </p>

      <h2>8. Third-party services</h2>
      <p>
        TeamNest may link to or integrate with third-party services (Zapier,
        Slack, Stripe, etc.). We are not responsible for those services or
        their terms. You use them at your own risk.
      </p>

      <h2>9. Termination</h2>
      <ul>
        <li>You may delete your account at any time from <strong>Settings → Profile → Delete Account</strong>.</li>
        <li>We may suspend or terminate your account if you breach these Terms, if required by law, or if continued use poses a security risk.</li>
        <li>On termination, your right to use the Service ends immediately. Sections 5, 6, 7, 10–14 survive termination.</li>
      </ul>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED <strong>"AS IS"</strong> AND
        <strong> "AS AVAILABLE"</strong> WITHOUT WARRANTIES OF ANY KIND,
        EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL
        BE UNINTERRUPTED, ERROR-FREE OR THAT AI OUTPUTS WILL BE ACCURATE.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, TEAMNEST AND ITS
        AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE,
        WHETHER INCURRED DIRECTLY OR INDIRECTLY. OUR TOTAL LIABILITY FOR ANY
        CLAIM ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED THE
        AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE EVENT GIVING
        RISE TO THE CLAIM, OR <strong>$100 USD</strong>, WHICHEVER IS GREATER.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold TeamNest harmless from any claim,
        damage or expense (including reasonable attorneys' fees) arising from
        your content, your violation of these Terms, or your violation of any
        third-party right.
      </p>

      <h2>13. Governing law &amp; dispute resolution</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, USA,
        without regard to its conflict-of-law principles. Any dispute will be
        resolved by binding arbitration in Wilmington, Delaware, under the
        Commercial Arbitration Rules of the American Arbitration Association,
        except you may seek injunctive relief in court for IP infringement.
        You waive any right to participate in a class action.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms occasionally. Material changes will be
        announced in-app and by email at least 14 days before they take
        effect. Continued use of the Service after the effective date
        constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href="mailto:legal@teamnest.ai">legal@teamnest.ai</a>. Postal mail:
        TeamNest, 1111B S Governors Ave Ste 6433, Dover DE 19904, USA.
      </p>
    </LegalLayout>
  );
}
